"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
  Badge,
  Table,
} from "reactstrap"
import { TicketPaymentService } from "../../../Service/TicketingService"
import type { TicketPayment, Ticket } from "./types"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  paymentData: TicketPayment | null
}

const TicketPaymentModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, paymentData }) => {
  const [form, setForm] = useState({
    tx_reference: "",
    operator: "MPESA",
    phone_number: "",
    amount: "",
    num_tickets: "1",
  })

  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (paymentData && isOpen) {
      // Viewing existing payment details
      setForm({
        tx_reference: paymentData.tx_reference,
        operator: paymentData.operator,
        phone_number: paymentData.phone_number,
        amount: paymentData.amount.toString(),
        num_tickets: paymentData.num_tickets.toString(),
      })
      fetchPaymentDetails(paymentData.id)
    } else if (!paymentData && isOpen) {
      // Creating new payment
      setForm({
        tx_reference: "",
        operator: "",
        phone_number: "",
        amount: "",
        num_tickets: "1",
      })
      setTickets([])
    }
    setErrors({})
  }, [paymentData, isOpen])

  const fetchPaymentDetails = async (paymentId: number) => {
    try {
      setLoadingDetails(true)
      const response = await TicketPaymentService.getById(paymentId)
      setTickets(response.tickets)
    } catch (error) {
      console.error("Failed to fetch payment details:", error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm({
      ...form,
      [name]: value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!form.tx_reference.trim()) {
      newErrors.tx_reference = "Transaction reference is required"
    }

    if (!form.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required"
    }

    if (!form.amount.trim()) {
      newErrors.amount = "Amount is required"
    } else if (isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      newErrors.amount = "Amount must be a valid positive number"
    }

    if (!form.num_tickets.trim()) {
      newErrors.num_tickets = "Number of tickets is required"
    } else if (isNaN(Number(form.num_tickets)) || Number(form.num_tickets) <= 0) {
      newErrors.num_tickets = "Number of tickets must be a positive number"
    }

    return newErrors
  }

  const handleSubmit = async () => {
    if (paymentData) {
      // Just close modal if viewing existing payment
      toggle()
      return
    }

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      const response = await TicketPaymentService.create({
        tx_reference: form.tx_reference,
        operator: form.operator,
        phone_number: form.phone_number,
        amount: Number(form.amount),
        num_tickets: Number(form.num_tickets),
      })

      if (response.success) {
        await Swal.fire({
          title: "Success!",
          text: `${response.total_tickets} tickets created successfully. SMS ${response.sms_sent ? "sent" : "failed"}.`,
          icon: "success",
          html: `
            <p>${response.total_tickets} tickets created successfully.</p>
            <p>SMS Status: ${response.sms_sent ? "Sent" : "Failed"}</p>
            <p><strong>Ticket Numbers:</strong></p>
            <p>${response.ticket_numbers.join(", ")}</p>
          `,
        })
        onSuccess()
      }
    } catch (error: any) {
      console.error("Failed to create payment:", error)
      if (error.response?.data?.tx_reference) {
        setErrors({ tx_reference: error.response.data.tx_reference[0] })
      } else {
        await Swal.fire("Error", error.response?.data?.message || "Something went wrong. Please try again.", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  const getTicketStatusBadge = (ticket: Ticket) => {
    return (
      <Badge color={ticket.is_used ? "danger" : "success"} pill>
        {ticket.is_used ? "Used" : "New"}
      </Badge>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{paymentData ? "Payment Details" : "Create Ticket Payment"}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="tx_reference">Transaction Reference *</Label>
          <Input
            name="tx_reference"
            value={form.tx_reference}
            onChange={handleChange}
            invalid={!!errors.tx_reference}
            placeholder="Enter transaction reference"
            disabled={!!paymentData}
          />
          <FormFeedback>{errors.tx_reference}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="operator">Operator *</Label>
          <Input type="select" name="operator" value={form.operator} onChange={handleChange} disabled={!!paymentData}>
            <option value="MTN">MTN Mobile Money</option>
            <option value="AIRTEL">AIRTEL MONEY</option>
            
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="phone_number">Phone Number *</Label>
          <Input
            name="phone_number"
            value={form.phone_number}
            onChange={handleChange}
            invalid={!!errors.phone_number}
            placeholder="Enter phone number"
            disabled={!!paymentData}
          />
          <FormFeedback>{errors.phone_number}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="amount">Amount (FRW) *</Label>
          <Input
            name="amount"
            type="number"
            value={form.amount}
            onChange={handleChange}
            invalid={!!errors.amount}
            placeholder="Enter amount"
            disabled={!!paymentData}
          />
          <FormFeedback>{errors.amount}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="num_tickets">Number of Tickets *</Label>
          <Input
            name="num_tickets"
            type="number"
            value={form.num_tickets}
            onChange={handleChange}
            invalid={!!errors.num_tickets}
            placeholder="Enter number of tickets"
            min="1"
            max="50"
            disabled={!!paymentData}
          />
          <FormFeedback>{errors.num_tickets}</FormFeedback>
        </FormGroup>

        {paymentData && (
          <>
            <hr />
            <h6>Generated Tickets</h6>
            {loadingDetails ? (
              <div className="text-center">
                <Spinner size="sm" />
                <span className="ms-2">Loading tickets...</span>
              </div>
            ) : (
              <Table striped size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ticket Number</th>
                    <th>Status</th>
                    <th>Used At</th>
                    <th>Turnstile</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket, index) => (
                    <tr key={ticket.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{ticket.ticket_number}</strong>
                      </td>
                      <td>{getTicketStatusBadge(ticket)}</td>
                      <td>
                        {ticket.used_at ? (
                          <small>{new Date(ticket.used_at).toLocaleString()}</small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {ticket.turnstile_info ? (
                          <Badge color="info">{ticket.turnstile_info.turnstile_number}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            {paymentData ? "Close" : "Cancel"}
          </Button>
          {!paymentData && (
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Create Payment"}
            </Button>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default TicketPaymentModal
